import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useState } from "react";

import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { formatNumber } from "src/utils/number/numberUtils";

import { useSAL2060_R01Store } from "./Store_SAL2060_R01";
import { defaultSAL2060_R01HeaderDto, SAL2060_R01HeaderDto } from "./Dto_SAL2060_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL2060_R01-header-grid-column-state';

interface HeaderTable_SAL2060_R01Props {
    gridRef: React.RefObject<AgGridReact<SAL2060_R01HeaderDto> | null>;
    onCustomerSelect: (customCd: string) => void;
}

export const Table_SAL2060_R01 = (props: HeaderTable_SAL2060_R01Props) => {
    const { gridRef, onCustomerSelect } = props;

    // ========================================
    // Store
    // ========================================
    const {
        headerList,
        headerColumnState,
        setHeaderColumnState,
        selectedCustomCd,
    } = useSAL2060_R01Store();

    // ========================================
    // State
    // ========================================
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    // ========================================
    // Grid 설정
    // ========================================
    const {
        settingRowIndex,
        createRowClassRules,
        paddedGridData,
    } = useGridSettingContent({
        gridRef,
        key: 'customCd',
        gridData: headerList,
        defaultGridData: {} as SAL2060_R01HeaderDto,
        selectedRow: undefined,
    });

    // ========================================
    // Row ID 생성
    // ========================================
    const getRowId = useCallback((params: any) => {
        const data = params.data;
        if (data && data.customCd) {
            return data.customCd;
        }
        return params.id;
    }, []);

    // ========================================
    // Cell Style
    // ========================================
    const getCellStyle = useCallback((params: any, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

    // ========================================
    // Header Names
    // ========================================
    const headerNames: Record<string, string> = {
        rowIndex: "",
        compNm: '사업장명',
        customCd: '거래처코드',
        customNm: '거래처명',
        salesAmt: '매출금액',
    };

    // ========================================
    // Column 정의
    // ========================================
    const baseColumnDefs: ColDef<SAL2060_R01HeaderDto>[] = useMemo(
        () => [
            ...getDefaultRowIndexColumnConfig(),
            {
                headerName: '사업장명',
                colId: 'compNm',
                field: 'compNm',
                width: 100,
                cellStyle: (params) => getCellStyle(params, 'compNm'),
                headerClass: aggridHeaderClass,
            },
            {
                headerName: '거래처코드',
                colId: 'customCd',
                field: 'customCd',
                width: 120,
                cellStyle: (params) => getCellStyle(params, 'customCd'),
                headerClass: aggridHeaderClass,
            },
            {
                headerName: '거래처명',
                colId: 'customNm',
                field: 'customNm',
                width: 180,
                cellStyle: (params) => getCellStyle(params, 'customNm'),
                headerClass: aggridHeaderClass,
            },
            {
                headerName: '매출금액',
                colId: 'salesAmt',
                field: 'salesAmt',
                width: 150,
                cellStyle: (params) => getCellStyle(params, 'salesAmt'),
                comparator: (valueA, valueB) => (valueA || 0) - (valueB || 0),
                valueFormatter: (params) =>
                    params.value ? formatNumber(params.value) : '0',
            },
        ],
        [hoveredRowIndex, headerList]
    );

    // ========================================
    // Custom Column 정의 (columnState 반영)
    // ========================================
    const columnDefs: ColDef<SAL2060_R01HeaderDto>[] = useMemo(() => {
        if (headerColumnState === null) {
            return baseColumnDefs;
        }

        return GridCustomColumnDefs<SAL2060_R01HeaderDto>({
            baseColumnDefs,
            columnState: headerColumnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [headerColumnState, baseColumnDefs]);

    // ========================================
    // Pinned Bottom Row (합계)
    // ========================================
    const pinnedBottomRow = useMemo(
        () => [
            {
                rowIndex: '',
                compNm: ``,
                customCd: '',
                customNm: `${headerList.length.toLocaleString()}건`,
                salesAmt: headerList
                    .reduce((sum, item) => sum + (item.salesAmt || 0), 0)
                    ,
            } as any,
        ],
        [headerList]
    );

    // ========================================
    // Row 클릭 핸들러
    // ========================================
    const handleRowClicked = useCallback(
        (event: any) => {
            // 행 클릭 시 search section 유지 (포커스 이동 방지)
            event.event?.preventDefault?.();
            event.event?.stopPropagation?.();
            
            const customCd = event.data?.customCd;
            if (customCd) {
                onCustomerSelect(customCd);
            }
        },
        [onCustomerSelect]
    );

    // ========================================
    // UI 렌더링
    // ========================================
    return (
        <GridContextWrapper<SAL2060_R01HeaderDto>
        	enableCellCalculation
            gridRef={gridRef}
            paddedGridData={paddedGridData as SAL2060_R01HeaderDto[]}
            initSelected={() => {}}
            defaultRowData={defaultSAL2060_R01HeaderDto}
            baseColumnDefs={columnDefs}
            setColumnState={setHeaderColumnState}
            localStorageKey={COLUMN_STATE_KEY}
            menuItems={undefined}
        >
            <Grid_Touch<SAL2060_R01HeaderDto>
                ref={gridRef}
                rowData={paddedGridData as SAL2060_R01HeaderDto[]}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={getRowId}
                rowClassRules={createRowClassRules<SAL2060_R01HeaderDto>({
                    idKey: 'customCd',
                    selectedList: selectedCustomCd ? [selectedCustomCd] : [],
                    deletedList: [],
                })}
                onRowClicked={handleRowClicked}
                onCellKeyDown={undefined}
                pinnedBottomRowData={pinnedBottomRow}
                setHoveredRowIndex={setHoveredRowIndex}
                columnStateKey={COLUMN_STATE_KEY}
                setColumnState={setHeaderColumnState}
            />
        </GridContextWrapper>
    );
};
